import {
  DONUT_GAP_DEGREES,
  DONUT_SIZE,
  DONUT_TRACK_WIDTH,
  PIECHART_COMPACT_CENTER,
  PIECHART_COMPACT_GAP_DEGREES,
  PIECHART_COMPACT_INNER_RADIUS,
  PIECHART_COMPACT_OUTER_RADIUS,
  PIECHART_FULL_CIRCLE_DEGREES,
  PIECHART_GLOW_PAD,
  PIECHART_SLICE_COLORS,
} from './piechart-constants';
import { describeArc, describeDonutSector, getAvailableDegrees } from './piechart-geometry';
import type {
  ParsedPieChart,
  PieChartArc,
  PieChartCompactSector,
  PieChartRenderModel,
  PieSlice,
} from './piechart-types';

interface PieArcBuildState {
  cursor: number;
  arcs: PieChartArc[];
}

interface CompactSectorBuildState {
  cursor: number;
  sectors: PieChartCompactSector[];
}

interface PieArcBuildInput {
  slices: PieSlice[];
  total: number;
  centerX: number;
  centerY: number;
  radius: number;
}

interface CompactSectorBuildInput {
  slices: PieSlice[];
  total: number;
}

function getPieSliceColor(index: number): string {
  return PIECHART_SLICE_COLORS[index % PIECHART_SLICE_COLORS.length];
}

function formatPercentage(percentage: number): string {
  return percentage % 1 === 0 ? String(percentage) : percentage.toFixed(1);
}

function getPieSliceDebugIdentity(input: {
  color: string;
  slice: PieSlice;
}): string {
  return `${input.slice.label}-${input.slice.value}-${input.color}`;
}

function buildPieChartArcs(input: PieArcBuildInput): PieChartArc[] {
  const sliceCount = input.slices.length;
  const capPixels = DONUT_TRACK_WIDTH / 2;
  const circumference = 2 * Math.PI * input.radius;
  const capDegrees = (capPixels / circumference) * PIECHART_FULL_CIRCLE_DEGREES;
  const availableDegrees = getAvailableDegrees({
    sliceCount,
    gapDegrees: DONUT_GAP_DEGREES,
    capDegrees,
  });

  return input.slices.reduce<PieArcBuildState>((state, slice, index) => {
    const color = getPieSliceColor(index);
    const sweepDeg = (slice.value / input.total) * availableDegrees;
    const gapAndCap = sliceCount > 1 ? DONUT_GAP_DEGREES / 2 + capDegrees : 0;
    const startDeg = state.cursor + gapAndCap;
    const endDeg = startDeg + sweepDeg;
    const nextCursor = state.cursor
      + sweepDeg
      + (sliceCount > 1 ? DONUT_GAP_DEGREES + capDegrees * 2 : 0);
    const percentage = formatPercentage((slice.value / input.total) * 100);

    return {
      cursor: nextCursor,
      arcs: [
        ...state.arcs,
        {
          pathData: describeArc({
            centerX: input.centerX,
            centerY: input.centerY,
            radius: input.radius,
            startDeg,
            endDeg,
          }),
          color,
          debugIdentity: getPieSliceDebugIdentity({ color, slice }),
          slice,
          percentage,
        },
      ],
    };
  }, { cursor: 0, arcs: [] }).arcs;
}

function buildCompactPieSectors(input: CompactSectorBuildInput): PieChartCompactSector[] {
  const sliceCount = input.slices.length;
  const availableDegrees = getAvailableDegrees({
    sliceCount,
    gapDegrees: PIECHART_COMPACT_GAP_DEGREES,
  });

  return input.slices.reduce<CompactSectorBuildState>((state, slice, index) => {
    const color = getPieSliceColor(index);
    const sweep = (slice.value / input.total) * availableDegrees;
    const halfGap = sliceCount > 1 ? PIECHART_COMPACT_GAP_DEGREES / 2 : 0;
    const start = state.cursor + halfGap;
    const end = start + sweep;
    const nextCursor = state.cursor + sweep + (sliceCount > 1 ? PIECHART_COMPACT_GAP_DEGREES : 0);

    return {
      cursor: nextCursor,
      sectors: [
        ...state.sectors,
        {
          pathData: describeDonutSector({
            centerX: PIECHART_COMPACT_CENTER,
            centerY: PIECHART_COMPACT_CENTER,
            outerRadius: PIECHART_COMPACT_OUTER_RADIUS,
            innerRadius: PIECHART_COMPACT_INNER_RADIUS,
            startDeg: start,
            endDeg: end,
          }),
          color,
          debugIdentity: getPieSliceDebugIdentity({ color, slice }),
        },
      ],
    };
  }, { cursor: 0, sectors: [] }).sectors;
}

export function buildPieChartRenderModel(parsed: ParsedPieChart): PieChartRenderModel | null {
  const total = parsed.slices.reduce((sum, slice) => sum + slice.value, 0);
  if (total === 0) return null;

  const centerX = DONUT_SIZE / 2;
  const centerY = DONUT_SIZE / 2;
  const radius = (DONUT_SIZE / 2) - DONUT_TRACK_WIDTH;

  return {
    total,
    centerX,
    centerY,
    radius,
    glowPad: PIECHART_GLOW_PAD,
    viewSize: DONUT_SIZE + PIECHART_GLOW_PAD * 2,
    arcs: buildPieChartArcs({
      slices: parsed.slices,
      total,
      centerX,
      centerY,
      radius,
    }),
    compactSectors: buildCompactPieSectors({
      slices: parsed.slices,
      total,
    }),
  };
}
